import { FiMessageSquare } from "react-icons/fi";
import DOMPurify from "dompurify";
import { PanelSupplierScores } from "newui/features/Rfx/Evaluation/types/scoring.type";
import { ScoringAccordion } from "../../../../ScoringAccordion";

interface ReadOnlyScoreTableProps {
	rfxId: number;
	suppliers: PanelSupplierScores[];
	onAddClarification?: (inviteId: number) => void;
}

const calcWeightedTotal = (criteria: PanelSupplierScores["criteria"]): string => {
	const scored = criteria.filter((c) => c.score !== null);
	if (scored.length === 0) {
		return "—";
	}
	const ws = scored.reduce((sum, c) => sum + (c.score ?? 0) * (c.criterion_weight / 100), 0);
	return ws % 1 === 0 ? String(ws) : ws.toFixed(1);
};

export const ReadOnlyScoreTable: React.FC<ReadOnlyScoreTableProps> = ({ suppliers, rfxId, onAddClarification }) => {
	const items = suppliers.map((s) => ({
		id: s.invite_id,
		logo: s.supplier_logo,
		name: s.supplier_name,
		score: calcWeightedTotal(s.criteria),
	}));

	return (
		<ScoringAccordion
			items={items}
			rfxId={rfxId}
			extraHeaderContent={onAddClarification ? (item) => (
				<button
					type="button"
					onClick={() => onAddClarification(item.id)}
					className="inline-flex items-center gap-1 rounded-md bg-[#0031DD] px-2.5 py-1 text-xs font-medium text-white hover:bg-[#0031DD]/90 transition-colors"
					data-testid={`add-clarification-${item.id}`}
				>
					<FiMessageSquare size={12} />
					Add Clarification
				</button>
			) : undefined}
		>
			{(_item, index) => {
				const supplier = suppliers[index];
				return (
					<div className="overflow-x-auto">
						<table className="w-full table-fixed border-collapse text-sm">
							<colgroup>
								<col className="w-[320px]" />
								<col className="w-[60px]" />
								<col className="w-[70px]" />
								<col className="w-[80px]" />
								<col />
							</colgroup>
							<thead>
								<tr className="text-left text-xs text-gray-400">
									<th className="px-4 py-2 font-medium">Criterion</th>
									<th className="px-4 py-2 font-medium text-right">Weight</th>
									<th className="px-4 py-2 font-medium text-right">Score</th>
									<th className="px-4 py-2 font-medium text-right">Weighted</th>
									<th className="px-4 py-2 font-medium">Comment</th>
								</tr>
							</thead>
							<tbody>
								{supplier.criteria.map((c) => (
									<tr
										key={c.rfx_criteria_id}
										className="border-t border-gray-50 align-top"
									>
										<td className="px-4 py-2 text-gray-700 break-words">
											{c.criterion_name}
										</td>
										<td className="px-4 py-2 text-right text-gray-500 tabular-nums">
											{c.criterion_weight}%
										</td>
										<td className="px-4 py-2 text-right font-medium tabular-nums">
											{c.score !== null ? c.score : "—"}
										</td>
										<td className="px-4 py-2 text-right font-medium tabular-nums text-gray-500">
											{c.score !== null
												? (() => { const ws = c.score * (c.criterion_weight / 100); return ws % 1 === 0 ? ws : ws.toFixed(1); })()
												: "—"}
										</td>
										<td className="px-4 py-2 text-gray-500 text-xs">
											{c.comment ? (
												<div
													className="prose prose-xs max-w-none [&>p]:m-0"
													dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(c.comment) }}
												/>
											) : "—"}
										</td>
									</tr>
								))}
							</tbody>
							<tfoot>
								<tr className="border-t border-gray-200">
									<td className="px-4 py-2 text-sm font-medium text-gray-900">Total</td>
									<td className="px-4 py-2" />
									<td className="px-4 py-2" />
									<td className="px-4 py-2 text-right font-bold tabular-nums text-gray-900 text-base">
										{calcWeightedTotal(supplier.criteria)}
									</td>
									<td className="px-4 py-2" />
								</tr>
							</tfoot>
						</table>
					</div>
				);
			}}
		</ScoringAccordion>
	);
};
